import { keepPreviousData, useQuery, useQueryClient, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { AxiosRequestConfig } from "axios";
import axiosClient from "../common/AxiosClient";
import { queryKeys } from "./queryKeys";

export interface UseAppQueryOptions<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData
> extends UseQueryOptions<TQueryFnData, TError, TData> {
  config?: AxiosRequestConfig;
}

const userQueryKey = queryKeys.users.current();

export function useIsUserReady(effectiveQueryKey: unknown): boolean {
  const queryClient = useQueryClient();
  const isUserQuery = JSON.stringify(effectiveQueryKey) === JSON.stringify(userQueryKey);
  return isUserQuery || queryClient.getQueryState(userQueryKey)?.status === 'success';
}

export function useAppQuery<TQueryFnData = unknown, TError = Error, TData = TQueryFnData>(
  url: string,
  options?: Partial<UseAppQueryOptions<TQueryFnData, TError, TData>>
): UseQueryResult<TData, TError> {
  const queryKey: [string, AxiosRequestConfig | undefined] = [url, options?.config?.params];
  const isUserReady = useIsUserReady(options?.queryKey ?? queryKey);

  return useQuery<TQueryFnData, TError, TData>({
    queryKey,
    queryFn: async () => {
      const res = await axiosClient.get(url, options?.config);

      return res.data;
    },
    placeholderData: keepPreviousData,
    ...options,
    enabled: isUserReady && (options?.enabled ?? true),
  });
}

export function useAppQueryPost<TQueryFnData = unknown, TError = unknown, TData = TQueryFnData>(
  url: string,
  options?: Partial<UseAppQueryOptions<TQueryFnData, TError, TData>>
): UseQueryResult<TData, TError> {
  const queryKey: [string, AxiosRequestConfig["params"], AxiosRequestConfig["data"]] = [
    url,
    options?.config?.params,
    options?.config?.data,
  ];
  const isUserReady = useIsUserReady(options?.queryKey ?? queryKey);

  return useQuery<TQueryFnData, TError, TData>({
    queryKey,
    queryFn: async () => {
      const { params, data = null, ...restConfig } = options?.config || {};
      const config = {
        ...restConfig,
        params
      };

      const res = await axiosClient.post(url, data, config);
      return res.data;
    },
    placeholderData: keepPreviousData,
    ...options,
    enabled: isUserReady && (options?.enabled ?? true),
  });
}
